
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Check } from 'lucide-react';
import { Form } from '@/components/ui/form';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useUserPortalRequisicoes } from '@/hooks/useUserPortalRequisicoes';
import { novaRequisicaoSchema, NovaRequisicaoFormData } from './schemas/novaRequisicaoSchema';
import { NovaRequisicaoFormFields } from './NovaRequisicaoFormFields';
import { NovaRequisicaoInfo } from './NovaRequisicaoInfo';
import { NovaRequisicaoActions } from './NovaRequisicaoActions';
import { DynamicCategoriaFields } from '@/components/Tickets/DynamicCategoriaFields';
import { useCategoriaCampos } from '@/hooks/useCategoriaCampos';

interface NovaRequisicaoModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const NovaRequisicaoModal = ({ isOpen, onClose }: NovaRequisicaoModalProps) => {
  const { createRequisicao } = useUserPortalRequisicoes();
  const [activeTab, setActiveTab] = useState('informacoes');

  const form = useForm<NovaRequisicaoFormData>({
    resolver: zodResolver(novaRequisicaoSchema),
    defaultValues: {
      titulo: '',
      descricao: '',
      urgencia: 'media',
      campos_customizados: {},
    },
  });

  const categoriaId = form.watch('categoria_id');
  const { campos } = useCategoriaCampos(categoriaId);

  const onSubmit = async (data: NovaRequisicaoFormData) => {
    try {
      const formData = {
        titulo: data.titulo,
        descricao: data.descricao || '',
        categoria_id: data.categoria_id || undefined,
        urgencia: data.urgencia,
        campos_customizados: data.campos_customizados,
      };
      
      await createRequisicao.mutateAsync(formData);
      form.reset();
      onClose();
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  const handleClose = () => {
    form.reset();
    setActiveTab('informacoes');
    onClose();
  };

  const steps = [
    { id: 'informacoes', label: 'Informações Básicas', description: 'Dados principais' },
    { id: 'campos-adicionais', label: 'Campos Adicionais', description: 'Informações extras' },
  ];

  const currentStepIndex = steps.findIndex(step => step.id === activeTab);

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto glass-effect border-2 border-blue-200">
        <DialogHeader className="space-y-4">
          <DialogTitle className="text-2xl font-display gradient-heading">
            Nova Requisição de Serviço
          </DialogTitle>
          
          {/* Wizard Steps */}
          <div className="flex items-center justify-between relative">
            {steps.map((step, index) => (
              <React.Fragment key={step.id}>
                <div className="flex flex-col items-center flex-1 relative z-10">
                  <div 
                    className={`w-12 h-12 rounded-full flex items-center justify-center font-bold transition-all duration-300 ${
                      index < currentStepIndex 
                        ? 'bg-gradient-to-br from-emerald-500 to-emerald-600 text-white shadow-lg scale-110' 
                        : index === currentStepIndex
                        ? 'bg-gradient-to-br from-blue-600 to-indigo-600 text-white shadow-xl scale-110 animate-pulse-slow'
                        : 'bg-slate-200 text-slate-500'
                    }`}
                  >
                    {index < currentStepIndex ? (
                      <Check className="h-6 w-6" />
                    ) : (
                      <span>{index + 1}</span>
                    )}
                  </div>
                  <div className="mt-2 text-center">
                    <p className={`text-sm font-semibold ${
                      index <= currentStepIndex ? 'text-slate-900' : 'text-slate-500'
                    }`}>
                      {step.label}
                    </p>
                    <p className="text-xs text-slate-500">{step.description}</p>
                  </div>
                </div>
                {index < steps.length - 1 && (
                  <div className="flex-1 h-1 mx-4 relative" style={{ top: '-2rem' }}>
                    <div className="absolute inset-0 bg-slate-200 rounded-full"></div>
                    <div 
                      className={`absolute inset-0 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-full transition-all duration-500 ${
                        index < currentStepIndex ? 'w-full' : 'w-0'
                      }`}
                    ></div>
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </DialogHeader>
        
        <Card className="border-0 shadow-none bg-transparent mt-6">
          <CardContent className="p-0">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                <Tabs value={activeTab} onValueChange={setActiveTab}>

                  <TabsContent value="informacoes" className="space-y-6 animate-fade-in">
                    <div className="bg-blue-50 border-2 border-blue-200 rounded-2xl p-6">
                      <NovaRequisicaoFormFields form={form} />
                    </div>
                    <NovaRequisicaoInfo />
                  </TabsContent>

                  <TabsContent value="campos-adicionais" className="space-y-6 animate-fade-in">
                    {categoriaId && campos.length > 0 ? (
                      <div className="bg-violet-50 border-2 border-violet-200 rounded-2xl p-6">
                        <DynamicCategoriaFields form={form} categoriaId={categoriaId} />
                      </div>
                    ) : (
                      <div className="text-center py-16 bg-slate-50 rounded-2xl border-2 border-slate-200">
                        <p className="text-slate-600 font-medium">
                          Selecione uma categoria para ver os campos adicionais
                        </p>
                      </div>
                    )}
                  </TabsContent>
                </Tabs>

                <NovaRequisicaoActions 
                  isLoading={createRequisicao.isPending}
                  onCancel={handleClose}
                />
              </form>
            </Form>
          </CardContent>
        </Card>
      </DialogContent>
    </Dialog>
  );
};
